// ============================================================================
// ADD/EDIT STAKEHOLDER MODAL
// Create or edit a project-scoped human engagement stakeholder.
// ============================================================================

import React, { useState } from 'react';
import { X, AlertCircle, User, Briefcase, Shield, Target, Hash, StickyNote } from 'lucide-react';
import { Stakeholder, StakeholderGovernanceLevel } from '../../types';
import { createStakeholder, updateStakeholder } from '../../services/stakeholdersService';

interface AddEditStakeholderModalProps {
  projectId: string;
  stakeholder?: Stakeholder | null;
  onClose: () => void;
  onSaved: (stakeholder: Stakeholder) => void;
}

const GOVERNANCE_LEVELS: { value: StakeholderGovernanceLevel; label: string }[] = [
  { value: 'sponsor', label: 'Sponsor' },
  { value: 'sign_off', label: 'Sign-off' },
  { value: 'consulted', label: 'Consulted' },
  { value: 'informed', label: 'Informed' },
];

export const AddEditStakeholderModal: React.FC<AddEditStakeholderModalProps> = ({
  projectId, stakeholder, onClose, onSaved
}) => {
  const isEdit = !!stakeholder;
  const [name, setName] = useState(stakeholder?.name || '');
  const [role, setRole] = useState(stakeholder?.role || '');
  const [governanceLevel, setGovernanceLevel] = useState<StakeholderGovernanceLevel>(
    stakeholder?.governanceLevel || 'consulted'
  );
  const [areaOfInterest, setAreaOfInterest] = useState(stakeholder?.areaOfInterest || '');
  const [signOffOrder, setSignOffOrder] = useState<string>(
    stakeholder?.signOffOrder !== undefined ? String(stakeholder.signOffOrder) : ''
  );
  const [notes, setNotes] = useState(stakeholder?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !role.trim()) {
      setError('Name and role are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const input = {
        name: name.trim(),
        role: role.trim(),
        governanceLevel,
        areaOfInterest: areaOfInterest.trim() || undefined,
        signOffOrder: signOffOrder.trim() ? Number(signOffOrder) : undefined,
        notes: notes.trim() || undefined,
      };

      const result = isEdit
        ? await updateStakeholder(projectId, stakeholder!.id, input)
        : await createStakeholder(projectId, input);

      onSaved(result.stakeholder);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save stakeholder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-[560px] w-full max-h-[90vh] flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <h3 className="text-base font-bold text-navy">
            {isEdit ? 'Edit Stakeholder' : 'New Stakeholder'}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto">
          {error && (
            <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="p-5 border-b border-[#e2e8f0] flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-slate-400" />Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-slate-400" />Role
                </label>
                <input
                  type="text"
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  placeholder="Head of Operations"
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-slate-400" />Governance level
              </label>
              <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                {GOVERNANCE_LEVELS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setGovernanceLevel(opt.value)}
                    className={`flex-1 py-2 text-xs font-semibold transition-all ${
                      governanceLevel === opt.value ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                <Target className="w-4 h-4 text-slate-400" />Area of interest
              </label>
              <input
                type="text"
                value={areaOfInterest}
                onChange={e => setAreaOfInterest(e.target.value)}
                placeholder="Budget approval, security posture, roadmap alignment…"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                <Hash className="w-4 h-4 text-slate-400" />Sign-off order
              </label>
              <input
                type="number"
                value={signOffOrder}
                onChange={e => setSignOffOrder(e.target.value)}
                placeholder="Lower signs off earlier"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
              <p className="mt-2 text-xs text-slate-400">Optional. Lower numbers sign off earlier in the sequence.</p>
            </div>
          </div>

          <div className="p-5">
            <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
              <StickyNote className="w-4 h-4 text-slate-400" />Notes
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Additional context on this stakeholder…"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Stakeholder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditStakeholderModal;
